import {
  BUILD_MODES,
  EXPERIENCE_LEVELS,
  EXPERIENCE_LEVEL_IDS,
  ROUND_COST_TO,
} from "@personnel/rules-leviathan";
import type { LeviathanCaptain, LeviathanDerived } from "@personnel/rules-leviathan";

interface BudgetPanelProps {
  captain: LeviathanCaptain;
  derived: LeviathanDerived;
  onChange: (patch: Partial<LeviathanCaptain>) => void;
}

function Figure({ label, value, tone = "normal" }: { label: string; value: string; tone?: "normal" | "over" }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs tracking-wide text-void-400 uppercase">{label}</span>
      <span
        className={`font-display text-lg font-semibold tabular-nums ${
          tone === "over" ? "text-red-400" : "text-void-200"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export function BudgetPanel({ captain, derived, onChange }: BudgetPanelProps) {
  const byLevel = captain.buildMode === "level";
  const bounded = derived.experienceAvailable !== null;
  const remaining = derived.experienceRemaining;

  return (
    <div className="flex flex-col gap-5 rounded-xl border border-void-800 bg-void-900/60 p-5">
      <div>
        <span className="text-xs tracking-wide text-void-400 uppercase">Build by</span>
        <div className="mt-2 flex flex-col gap-1 rounded-lg border border-void-800 p-1">
          {BUILD_MODES.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => onChange({ buildMode: id })}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                captain.buildMode === id
                  ? "bg-leviathan-600 text-void-950"
                  : "text-void-400 hover:text-void-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {byLevel && (
        <label className="flex flex-col gap-2">
          <span className="text-xs tracking-wide text-void-400 uppercase">Experience Level</span>
          <select
            value={captain.experienceLevel ?? ""}
            onChange={(event) =>
              onChange({
                experienceLevel:
                  event.target.value === ""
                    ? null
                    : (event.target.value as NonNullable<LeviathanCaptain["experienceLevel"]>),
              })
            }
            className="rounded-md border border-void-800 bg-void-900 px-3 py-2 text-sm text-void-200 focus:border-leviathan-600 focus:outline-none"
          >
            <option value="">Choose…</option>
            {EXPERIENCE_LEVEL_IDS.map((id) => {
              const level = EXPERIENCE_LEVELS[id];
              return (
                <option key={id} value={id}>
                  {level.name} — {level.experienceAvailable} XP
                  {level.minimumRank > 0 ? `, Rank ${level.minimumRank}+` : ""}
                </option>
              );
            })}
          </select>
        </label>
      )}

      {captain.buildMode === "points" && (
        <label className="flex flex-col gap-2">
          <span className="text-xs tracking-wide text-void-400 uppercase">
            Experience Points
          </span>
          <input
            type="number"
            min={0}
            value={captain.experiencePoints ?? ""}
            placeholder="Total earned"
            onChange={(event) => {
              const next = Number.parseInt(event.target.value, 10);
              onChange({ experiencePoints: Number.isNaN(next) ? null : Math.max(0, next) });
            }}
            className="rounded-md border border-void-800 bg-void-900 px-3 py-2 text-sm tabular-nums text-void-200 placeholder:text-void-700 focus:border-leviathan-600 focus:outline-none"
          />
        </label>
      )}

      <div className="flex flex-col gap-2 border-t border-void-800 pt-4">
        <Figure label="Attributes" value={String(derived.attributeCost)} />
        <Figure label="Qualities" value={String(derived.qualityCost)} />
        <div className="my-1 border-t border-void-800" />
        <Figure
          label="Total Cost"
          value={String(derived.totalCost)}
          tone={remaining !== null && remaining < 0 ? "over" : "normal"}
        />
        {captain.showRoundedCost && (
          <Figure label="Rounded Cost" value={String(derived.roundedCost)} />
        )}
        {derived.essenceCost > 0 && (
          <>
            <Figure label="Essence" value={String(derived.essenceCost)} />
            <Figure label="XP committed" value={String(derived.experienceCommitted)} />
          </>
        )}
        {bounded && (
          <Figure
            label={
              remaining !== null && remaining < 0
                ? "Over budget"
                : derived.unspentIsLost
                  ? "Forfeited"
                  : "Available"
            }
            value={
              remaining === null ? "—" : remaining < 0 ? String(Math.abs(remaining)) : String(remaining)
            }
            tone={remaining !== null && remaining < 0 ? "over" : "normal"}
          />
        )}
      </div>

      <label className="flex items-center justify-between gap-3 border-t border-void-800 pt-4">
        <span className="text-xs leading-relaxed text-void-400">
          Display Rounded Cost
          <span className="block text-void-700">
            Rounds to the nearest {ROUND_COST_TO}, as printed cards do.
          </span>
        </span>
        <input
          type="checkbox"
          checked={captain.showRoundedCost}
          onChange={(event) => onChange({ showRoundedCost: event.target.checked })}
          className="size-4 shrink-0 accent-leviathan-600"
        />
      </label>

      {derived.effectiveRank !== captain.attributes.rank && (
        <p className="rounded-md border border-leviathan-600/30 bg-leviathan-600/5 px-3 py-2 text-xs text-void-400">
          Effective Rank is{" "}
          <span className="font-semibold text-leviathan-500">{derived.effectiveRank}</span> — raised
          by a Quality.
        </p>
      )}
    </div>
  );
}
